import { useCallback, useMemo, useState } from 'react';
import { RequestStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { ScheduleRequestStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import {
  EventLocationType,
  EventStatus,
  EventType,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { SendRequest } from '@weave/schema-gen-ts/dist/schemas/sms/send/v3/send_service.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import dayjs from 'dayjs';
import { startCase } from 'lodash-es';
import { useMutation } from 'react-query';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { ScheduleQueries, ScheduleTypes } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ServiceProvidersQueries } from '@frontend/api-service-providers';
import { checkAggregateACL, getUser } from '@frontend/auth-helpers';
import { ActionsUI } from '@frontend/contact-actions';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { formatDateTime, useLocationTimezone } from '@frontend/schedule-helpers';
import { SchemaSMSSendService } from '@frontend/schema';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { DATETIME_FORMATS, MIN_APPT_TYPE_DURATION_IN_MINUTES } from '../../../../../../constants';
import { useManageAppointmentWriteback } from '../../../../../../hooks/scheduler-v3/use-manage-appointment-writeback';
import { useSchedulingLocationInfo } from '../../../../../../hooks/use-scheduling-location-info';
import { usePersonSelector } from '../../../../components/PersonSelector/usePersonSelector';
import { useScheduleActionsContext } from '../../ScheduleActionsContext';
import {
  defaultTemplate,
  successIntegrateOfficeWithNoWritebackMessage,
  successIntegratedMessage,
  successNonIntegrateOfficeMessage,
} from '../constant';
import { useScheduleRequestModalContext } from '../Context/ScheduleRequestModalContext';
import { ScheduleRequestModalFormContext } from '../Context/ScheduleRequestModalFormContext';
import {
  BackendError,
  EMPTY_BACKEND_ERROR,
  extractBackendError,
} from '../ScheduleRequestModal/ScheduleRequestModalManager/BackendErrorDetails';
import {
  CreateAppointmentRequestParamsType,
  CreateWritebackAppointmentV3PayloadArgs,
  ScheduleRequestModalTypeEnum,
} from '../ScheduleRequestModal/types';
import { getRequestTags, replaceTemplateTags } from '../utils';
import { useApproveScheduleRequestQuery } from './useApproveScheduleRequestQuery';
import { useManageScheduleRequestModalDetails } from './useManageScheduleRequestModalDetails';
import { useManageScheduleRequestModalForm } from './useManageScheduleRequestModalForm';
import { ReducedAppointmentType, ReducedPractitioner } from './useReviewRequest';

export const useScheduleRequestDetails = () => {
  const { t } = useTranslation('scheduleCalendarRequest');
  const { isScheduleV3FlagEnabled: isV3Enabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const {
    handleCloseScheduleRequestModal,
    refetchScheduleRequestList,
    scheduleRequestModalProps,
    selectedScheduleRequest,
  } = useScheduleRequestModalContext();

  const invalidateAppointmentsV2 = ServiceProvidersQueries.useInvalidateListAppointments();

  const { formatDateAndTime } = useLocationTimezone(selectedScheduleRequest.locationId);

  const alert = useAlert();
  const { selectedLocationIds, getScopeName, getLocationTimeZone } = useSchedulingLocationInfo();
  const { closeModal: closeActionsModal, refetchAppointmentRequestCount } = useScheduleActionsContext();
  const canSendMessages = getFeatureFlagValue('nwx:messaging-integrated-messaging-fe-acl-enforcement', 'ALL')
    ? !!checkAggregateACL(selectedLocationIds, Permission.SMS_INBOX_MANAGE, 'any')?.aggregateValue
    : true;

  const [modalType, setModalType] = useState<ScheduleRequestModalTypeEnum | ''>('');
  const [backendError, setBackendError] = useState<BackendError>(EMPTY_BACKEND_ERROR);

  const [template, setTemplate] = useState<string>(defaultTemplate);
  const [shouldSendMessageV3, setShouldSendMessageV3] = useState(true);

  const manageScheduleRequestModalDetails = useManageScheduleRequestModalDetails(selectedScheduleRequest);

  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();

  const isSourceRequired = isIntegratedOffice;

  const isWorkstationRequired = isIntegratedOffice && !!manageScheduleRequestModalDetails.workstations.length;

  const { personSelectorDialogProps, selectedPerson, personSelectorProps } = usePersonSelector({
    placement: 'bottom-start',
    initialOffset: { x: 0, y: 45 },
    formFields: {
      firstName: { value: selectedScheduleRequest?.schedulee?.firstName ?? '', required: true },
      lastName: { value: selectedScheduleRequest?.schedulee?.lastName ?? '', required: true },
      phoneNumber: { value: selectedScheduleRequest?.schedulee?.phoneNumber ?? '', required: true },
      email: { value: selectedScheduleRequest?.schedulee?.email ?? '', required: true },
      dateOfBirth: {
        value: formatDate(selectedScheduleRequest?.schedulee?.birthDate, 'MM/DD/YYYY'),
        required: isIntegratedOffice,
      },
      gender: { value: startCase(selectedScheduleRequest?.schedulee?.gender ?? ''), required: isIntegratedOffice },
      source: { value: '', required: isSourceRequired },
      clientLocationId: { value: '', required: isWorkstationRequired },
      street: { value: selectedScheduleRequest?.schedulee?.address?.street ?? '', required: isIntegratedOffice },
      city: { value: selectedScheduleRequest?.schedulee?.address?.city ?? '', required: isIntegratedOffice },
      state: { value: selectedScheduleRequest?.schedulee?.address?.state ?? '', required: isIntegratedOffice },
      country: { value: selectedScheduleRequest?.schedulee?.address?.country ?? '', required: isIntegratedOffice },
      postalCode: {
        value: selectedScheduleRequest?.schedulee?.address?.postalCode ?? '',
        required: isIntegratedOffice,
      },
    },
    clientLocationIds: manageScheduleRequestModalDetails?.access?.ClientLocations || [],
  });

  const personId = selectedPerson?.PersonID || '';
  const locationId = selectedScheduleRequest?.locationId || '';
  const locationTimezone = getLocationTimeZone(locationId);
  const phoneNumber = selectedScheduleRequest?.schedulee?.phoneNumber || selectedPerson?.MobilePhone || '';

  const {
    createAppointmentWriteback,
    isLoadingCreateWritebackAppointment,
    isLoadingAppointmentStatusData,
    isLoadingDataSources,
    isLoadingSourceTenants,
    appointmentWritebackSourceTenants,
    patientWritebackSourceTenants,
    checkWritebackCapability,
    getAppointmentExternalStatusForSourceTenant,
  } = useManageAppointmentWriteback({
    isEnabled: isIntegratedOffice,
    locationId,
  });

  const { appointmentTypes, providers } = manageScheduleRequestModalDetails;

  const manageScheduleRequestFormDetails = useManageScheduleRequestModalForm(
    selectedScheduleRequest,
    !!appointmentWritebackSourceTenants.length,
    appointmentTypes,
    providers
  );

  const hasAppointmentWritebacks = checkWritebackCapability(
    manageScheduleRequestFormDetails.scheduleRequestApprovalForm.values.source || ''
  );

  const { mutateAsync: createScheduleAppointment, isLoading: isLoadingCreateAppointment } =
    ScheduleQueries.useCreateAppointment();

  const createCalendarEventMutation = SchedulerV3.Mutations.useCreateCalendarEventMutation();

  const { approveScheduleRequest, isLoading: isUpdateScheduleRequestLoading } = useApproveScheduleRequestQuery();

  const { mutateAsync: sendSMS, isLoading: isSendSMSLoading } = useMutation({
    mutationFn: (req: SendRequest) => SchemaSMSSendService.Send(req),
    onError: () => {
      alert.error('Failed to send message');
    },
  });

  const { onClick: messageOnClick } = ActionsUI.actions.useMessageAction();

  const handleCloseModal = () => {
    scheduleRequestModalProps.closeModal();
    handleCloseScheduleRequestModal();
    refetchScheduleRequestList();
    refetchAppointmentRequestCount();
    invalidateAppointmentsV2();
  };

  const getScheduleRequestCustomDateAndTime = () => {
    const { customDateField, customStartTimeField } = manageScheduleRequestFormDetails;
    if (customDateField.value && customStartTimeField.value) {
      const date = dayjs(customDateField.value).format('MM/DD/YYYY');
      const time = dayjs(`${date} ${customStartTimeField.value}`).format('hh:mm a');
      return `${date} ${time}`;
    }
    return '';
  };

  const getApproveScheduleRequestData = useCallback(() => {
    const { selectedPerson, selectedApptType, selectedProviderId, startDate, appointmentDuration } =
      getAppointmentRequestFormDetails();
    const approveData: ScheduleTypes.UpdateScheduleRequestType['input'] = {
      status: RequestStatus.ACCEPTED,
      reviewedBy: getUser()?.userID,
      id: selectedScheduleRequest?.id ?? '',
      locationId: selectedScheduleRequest?.locationId ?? '',
      personId: selectedPerson?.PersonID ?? '',
      appointmentTypeId: selectedApptType?.id ?? '',
      practitionerId: selectedProviderId ?? '',
      bookingDuration: appointmentDuration.toString() ?? selectedApptType?.durationMinutes?.toString() ?? '0',
      dateTime: isV3Enabled
        ? formatDateTime(startDate, {
            fromTimezone: locationTimezone,
            toTimezone: 'UTC',
            outputFormat: 'YYYY-MM-DDTHH:mm:ss[Z]',
          })
        : dayjs(startDate).toISOString(),
      scheduleRequestStatus: ScheduleRequestStatus.ACCEPTED,
    };
    return approveData;
  }, [manageScheduleRequestModalDetails, manageScheduleRequestFormDetails, selectedPerson]);

  const getIsAppointmentDateValid = () => {
    const { appointmentDuration } = manageScheduleRequestFormDetails;
    const requestedDate = new Date(selectedScheduleRequest?.dateTime ?? '');
    const currentDate = dayjs();
    const validDate = dayjs(requestedDate).isAfter(currentDate);
    const newCustomDate = getScheduleRequestCustomDateAndTime();
    const isCustomDateValid = dayjs(newCustomDate).isAfter(currentDate);
    const isEndTimeValid = shouldHaveCustomFieldValues ? appointmentDuration > 0 : true;

    return validDate || isCustomDateValid || isEndTimeValid;
  };

  const getSendSMSForScheduleRequestData = useCallback(() => {
    const hasCustomDate = manageScheduleRequestFormDetails.scheduleRequestApprovalForm.values.dateAndTime === 'custom';

    const newCustomDate = getScheduleRequestCustomDateAndTime();
    const selectedDateTime =
      manageScheduleRequestFormDetails.scheduleRequestApprovalForm.values.dateAndTime ||
      selectedScheduleRequest?.dateTime ||
      '';

    const selectedDateTimeTz = formatDateAndTime(selectedDateTime, 'MM/DD/YYYY h:mm a');

    const datetime = hasCustomDate ? newCustomDate : selectedDateTimeTz ?? '';
    const locationDateTime = isV3Enabled
      ? dayjs(datetime).format('MM/DD/YYYY h:mm a')
      : formatDateTime(datetime, {
          fromTimezone: dayjs.tz.guess(),
          toTimezone: locationTimezone,
          outputFormat: 'MM/DD/YYYY h:mm a',
        });

    // Return if locationDateTime is not available
    if (!locationDateTime) return;

    const tags = getRequestTags(locationDateTime, selectedScheduleRequest?.schedulee, getScopeName(locationId));
    const templateText = replaceTemplateTags(template, tags.map);
    const mobilePhone = selectedPerson?.MobilePhone
      ? selectedPerson.MobilePhone
      : selectedScheduleRequest?.schedulee?.phoneNumber ?? '';
    const messageOptions: SendRequest = {
      locationId: selectedScheduleRequest?.locationId ?? '',
      programSlugId: 'manual-messages',
      personPhone: mobilePhone,
      body: templateText,
      shortenUrls: true,
      createdBy: getUser()?.userID,
    };
    return messageOptions;
  }, [
    getScheduleRequestCustomDateAndTime,
    getScopeName,
    isV3Enabled,
    locationId,
    locationTimezone,
    manageScheduleRequestFormDetails.scheduleRequestApprovalForm.values.dateAndTime,
    selectedPerson?.MobilePhone,
    selectedScheduleRequest?.dateTime,
    selectedScheduleRequest?.locationId,
    selectedScheduleRequest?.schedulee,
    template,
  ]);

  const getApproveScheduleRequestAndSMSRequestData = useCallback(() => {
    const approveScheduleRequestData = getApproveScheduleRequestData();
    const sendSMSForScheduleRequestData = getSendSMSForScheduleRequestData();
    return {
      approveScheduleRequestData,
      sendSMSForScheduleRequestData,
    };
  }, [getSendSMSForScheduleRequestData, getApproveScheduleRequestData]);

  const validateAndApproveRequest = async (appointmentId = '', writebackWasSkipped = false) => {
    const isAppointmentDateValid = getIsAppointmentDateValid();

    if (isAppointmentDateValid) {
      const { approveScheduleRequestData, sendSMSForScheduleRequestData } =
        getApproveScheduleRequestAndSMSRequestData();

      const requestVersion = selectedScheduleRequest.linkHash;
      const shouldSendSMS = (!isV3Enabled || shouldSendMessageV3) && sendSMSForScheduleRequestData;

      Promise.all([
        approveScheduleRequest(
          {
            appointmentId,
            ...approveScheduleRequestData,
          },
          requestVersion
        ),
        ...(shouldSendSMS ? [sendSMS(sendSMSForScheduleRequestData)] : []),
      ])
        .then((res) => {
          if (res) {
            let message;
            if (hasAppointmentWritebacks && !writebackWasSkipped) {
              message = successIntegratedMessage;
            } else if (isIntegratedOffice) {
              message = successIntegrateOfficeWithNoWritebackMessage;
            } else {
              message = successNonIntegrateOfficeMessage;
            }
            alert.success(message);
            setModalType(ScheduleRequestModalTypeEnum.MESSAGE_SENT);
            invalidateAppointmentsV2();
          }
        })
        .catch((err) => {
          if (err?.message?.includes('phone')) {
            setModalType(ScheduleRequestModalTypeEnum.MESSAGE_ERROR);
          } else {
            setBackendError(extractBackendError(err));
            setModalType(ScheduleRequestModalTypeEnum.ERROR);
          }
        });
    } else {
      setModalType(ScheduleRequestModalTypeEnum.ERROR);
    }
  };

  const getAppointmentRequestFormDetails = useCallback((): CreateAppointmentRequestParamsType => {
    const { appointmentTypes, providers } = manageScheduleRequestModalDetails;

    const {
      scheduleRequestApprovalForm: { values },
      customDateField,
      customStartTimeField,
      appointmentDuration,
    } = manageScheduleRequestFormDetails;

    const selectedProviderId = values?.provider;
    const selectedApptType = appointmentTypes.find((item) =>
      'bookableAppointmentTypeId' in item
        ? item.bookableAppointmentTypeId === values.appointmentType
        : item.id === values.appointmentType
    );
    const selectedWorkstationId = values?.workstation;
    const selectedProvider = providers.find((item) => item.id === selectedProviderId);

    const getStartDate = () => {
      if (values.dateAndTime === 'custom') {
        const justDate = dayjs(customDateField.value).format('MM/DD/YYYY');
        const dateTime = dayjs(`${justDate} ${customStartTimeField.value}`).format('MM/DD/YYYY hh:mm a');
        return dateTime;
      }
      return isV3Enabled
        ? formatDateAndTime(values.dateAndTime, 'YYYY-MM-DD h:mm A')
        : dayjs(values.dateAndTime).toISOString();
    };

    return {
      selectedPerson,
      selectedProviderId: selectedProviderId ?? '',
      selectedApptType,
      selectedWorkstationId,
      startDate: getStartDate(),
      sourceId: values.source ?? '',
      selectedProviderName: selectedProvider ? `${selectedProvider.firstName} ${selectedProvider.lastName}` : '',
      appointmentDuration,
    };
  }, [manageScheduleRequestModalDetails, manageScheduleRequestFormDetails, selectedPerson]);

  const getCalendarEventRequestBody = ({
    selectedPerson,
    selectedApptType,
    selectedProviderId,
    selectedWorkstationId,
    startDate: date,
    sourceId,
    selectedProviderName,
    appointmentDuration,
  }: CreateAppointmentRequestParamsType) => {
    const { providers } = manageScheduleRequestModalDetails;
    const selectedProvider = providers.find((item) => item.id === selectedProviderId) as ReducedPractitioner;
    const calendarId = selectedProvider?.calendarId || '';

    let duration, startDate, startTime, endDate, endTime;

    if (isV3Enabled) {
      duration = appointmentDuration ?? selectedApptType?.durationMinutes ?? 0;
      startDate = formatDateTime(date, {
        fromTimezone: locationTimezone,
        toTimezone: 'UTC',
        outputFormat: 'YYYY-MM-DD',
      });
      startTime = formatDateTime(date, {
        fromTimezone: locationTimezone,
        toTimezone: 'UTC',
        outputFormat: 'HH:mm:ss',
      });
      const endDateTime = dayjs(date).add(Number(duration), 'minute');
      endDate = formatDateTime(endDateTime, {
        fromTimezone: locationTimezone,
        toTimezone: 'UTC',
        outputFormat: 'YYYY-MM-DD',
      });
      endTime = formatDateTime(endDateTime, {
        fromTimezone: locationTimezone,
        toTimezone: 'UTC',
        outputFormat: 'HH:mm:ss',
      });
    } else {
      duration = appointmentDuration ?? selectedApptType?.durationMinutes ?? 0;
      const startDateTime = dayjs(date).tz('UTC');
      startDate = startDateTime.format('YYYY-MM-DD');
      endDate = startDate;
      startTime = startDateTime.format('HH:mm:ss');
      endTime = startDateTime.add(duration, 'm').tz('UTC').format('HH:mm:ss');
    }

    const createCalendarEventRequestBody: SchedulerV3.Types.CreateCalendarEventIO['input'] = {
      organizerCalendarId: calendarId,
      organizerId: selectedProviderId,
      locationId: locationId,
      sourceTenantId: sourceId || locationId, // LocationId is used in case of non-integrated office
      locationType: EventLocationType.OFFICE,
      eventType: EventType.APPOINTMENT,
      referenceTypeId: selectedApptType?.id,
      attendeeId: selectedPerson?.PersonID || '',
      attendeeStatus: EventStatus.CONFIRMED,
      startDate,
      startTime,
      endDate,
      endTime,
      details: {
        orNa: selectedProviderName,
        dur: duration,
        rTyNa: selectedApptType?.name,
      },
      ...(selectedWorkstationId ? { operatorId: selectedWorkstationId } : {}),
      bookingSubmissionId: selectedScheduleRequest?.id || '',
    };

    return createCalendarEventRequestBody;
  };

  const getCreateAppointmentRequestBody = ({
    selectedPerson,
    selectedApptType,
    selectedProviderId,
    selectedWorkstationId,
    startDate,
    sourceId: sourceFieldId,
    selectedProviderName,
    appointmentDuration,
  }: CreateAppointmentRequestParamsType) => {
    /* In review form, `source` field is used for both sourceId and sourceTenantId in case of non-integrated and integrated office resp.
      For integrated office, sourceTenantId is used to get sourceId from selected `source` field (sourceTenant) value
      For non-integrated office, sourceId is used as directly from `source` field value and sourceTenantId will be empty
     */
    let sourceId = sourceFieldId;
    let sourceTenantId = '';

    if (isIntegratedOffice) {
      sourceTenantId = sourceFieldId;
      sourceId =
        appointmentWritebackSourceTenants.find((sourceTenant) => {
          return sourceTenant.sourceTenantId === sourceTenantId;
        })?.sourceId || '';
    }

    const createAppointmentRequestBody: ScheduleTypes.ScheduleCreateAppointmentType['input'] = {
      appointment: {
        customerIds: [selectedPerson?.PersonID ?? ''],
        start: dayjs(startDate).toISOString(),
        providerIds: selectedProviderId ? [selectedProviderId] : [],
        workstationIds: selectedWorkstationId ? [selectedWorkstationId] : [],
        duration: appointmentDuration ?? selectedApptType?.durationMinutes,
        type: selectedApptType?.id,
        createdBySourceId: sourceId ?? '',
        createdByLocationId: locationId ?? '',
        locationIds: [locationId],
        practitionerName: selectedProviderName,
        clientLocationId: selectedPerson?.ClientLocationID,
        sourceTenantId: sourceTenantId ?? '',
      },
    };
    return createAppointmentRequestBody;
  };

  const createAppointmentWritebackV3Payload = ({
    selectedPerson,
    selectedApptType,
    appointmentDuration,
    ...restDetails
  }: CreateWritebackAppointmentV3PayloadArgs) => {
    const { providers, workstations } = manageScheduleRequestModalDetails;
    const selectedProvider = providers.find((item) => item.id === restDetails.selectedProviderId);
    const selectedOperatory = workstations.find((item) => item.id === restDetails.selectedWorkstationId);
    const appointmentExternalStatus = getAppointmentExternalStatusForSourceTenant(restDetails.sourceId);
    const appointmentDateTime = isV3Enabled
      ? formatDateTime(restDetails.startDate, {
          fromTimezone: locationTimezone,
          toTimezone: 'UTC',
          outputFormat: DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET,
        })
      : dayjs(restDetails.startDate).utc().format();

    return {
      locationId: locationId,
      sourceTenantId: restDetails.sourceId,
      personFullName: getFullName(selectedPerson),
      personExternalId: selectedPerson.PMID,
      providerExternalId: selectedProvider?.externalId || '',
      appointmentTypeExternalId: selectedApptType?.externalId || '',
      operatoryExternalId: selectedOperatory?.externalId,
      externalStatus: appointmentExternalStatus || '',
      durationMinutes: appointmentDuration || selectedApptType?.durationMinutes || MIN_APPT_TYPE_DURATION_IN_MINUTES,
      appointmentDateTime,
      amount: selectedApptType?.amount,
      createdAt: dayjs().toISOString(),
      bookingSubmissionId: selectedScheduleRequest?.id || '',
    };
  };

  const handlePostApprove = async () => {
    const isAppointmentDateValid = getIsAppointmentDateValid();
    const { selectedPerson, selectedApptType, appointmentDuration, ...restDetails } =
      getAppointmentRequestFormDetails();
    // TODO: Check whether we need to add a hasApptWritebacks check here
    if (isAppointmentDateValid) {
      if (selectedPerson?.PersonID && selectedApptType) {
        try {
          if (isV3Enabled) {
            const selectedApptTypeV3 = selectedApptType as ReducedAppointmentType;

            if (isIntegratedOffice) {
              // @TODO: Add timezone handling for v3 integrated office
              // Check if the selected source has writeback capability
              const hasWritebackCapability = checkWritebackCapability(
                manageScheduleRequestFormDetails.scheduleRequestApprovalForm.values.source || ''
              );

              if (hasWritebackCapability) {
                try {
                  const payload = createAppointmentWritebackV3Payload({
                    selectedPerson,
                    selectedApptType: selectedApptTypeV3,
                    appointmentDuration,
                    ...restDetails,
                  });
                  await createAppointmentWriteback(payload);
                  await validateAndApproveRequest();
                } catch (error) {
                  setBackendError(extractBackendError(error));
                  setModalType(ScheduleRequestModalTypeEnum.WRITEBACK_ERROR);
                }
              } else {
                setModalType(ScheduleRequestModalTypeEnum.WRITEBACK_ERROR);
              }
            } else {
              const payload = getCalendarEventRequestBody({
                selectedApptType,
                selectedPerson,
                appointmentDuration,
                ...restDetails,
              });
              const calendarEventData = await createCalendarEventMutation.mutateAsync(payload);
              await validateAndApproveRequest(calendarEventData?.event?.id || '');
            }
          } else {
            const payload = getCreateAppointmentRequestBody({
              selectedApptType,
              selectedPerson,
              appointmentDuration,
              ...restDetails,
            });

            const appointmentData = await createScheduleAppointment(payload);
            await validateAndApproveRequest(appointmentData?.appointment?.id || '');
          }
        } catch (err) {
          const errorAlertMessage = hasAppointmentWritebacks
            ? t('Failed to write back appointment')
            : t('Failed to create appointment');
          alert.error(errorAlertMessage);
          setBackendError(extractBackendError(err));
          setModalType(
            hasAppointmentWritebacks ? ScheduleRequestModalTypeEnum.WRITEBACK_ERROR : ScheduleRequestModalTypeEnum.ERROR
          );
        }
      }
    } else {
      alert.error(t('Invalid date and time. Please select a future date and time.'));
    }
  };

  const providerValue = useMemo<ScheduleRequestModalFormContext>(() => {
    return {
      manageScheduleRequestFormDetails,
      manageScheduleRequestModalDetails,
      personSelectorDialogProps,
      setTemplate,
      hasAppointmentWritebacks,
      isIntegratedOffice,
      selectedPerson,
      personSelectorProps,
      shouldSendMessageV3,
      setShouldSendMessageV3,
    };
  }, [
    manageScheduleRequestFormDetails,
    manageScheduleRequestModalDetails,
    personSelectorDialogProps,
    hasAppointmentWritebacks,
    isIntegratedOffice,
    selectedPerson,
    personSelectorProps,
    shouldSendMessageV3,
    setShouldSendMessageV3,
  ]);

  const isLoading =
    isLoadingDataSources ||
    isLoadingSourceTenants ||
    isLoadingCreateWritebackAppointment ||
    isLoadingCreateAppointment ||
    createCalendarEventMutation.isLoading ||
    isLoadingAppointmentStatusData ||
    isUpdateScheduleRequestLoading ||
    isSendSMSLoading;

  const shouldHaveCustomFieldValues =
    manageScheduleRequestFormDetails.scheduleRequestApprovalForm.values.dateAndTime === 'custom';

  const hasCustomFieldValues = !!(
    manageScheduleRequestFormDetails.customDateField.value &&
    manageScheduleRequestFormDetails.customStartTimeField.value &&
    manageScheduleRequestFormDetails.customEndTimeField.value
  );

  const disableApproveBtn =
    !selectedPerson ||
    !manageScheduleRequestFormDetails.scheduleRequestApprovalForm.isComplete ||
    (shouldHaveCustomFieldValues && !hasCustomFieldValues) ||
    isLoading;

  const fullName = selectedPerson ? getFullName(selectedPerson) : getFullName(selectedScheduleRequest.schedulee ?? {});

  return {
    modalType,
    setModalType,
    backendError,
    handleCloseScheduleRequestModal,
    validateAndApproveRequest,
    isLoading,
    selectedPerson,
    locationId,
    phoneNumber,
    personId,
    closeActionsModal,
    fullName,
    disableApproveBtn,
    providerValue,
    handlePostApprove,
    messageOnClick,
    patientWritebackSourceTenants,
    handleCloseModal,
    canSendMessages,
    scheduleRequestModalProps,
    appointmentWritebackSourceTenants,
    isV3Enabled,
    isLoadingSourceTenants,
  };
};
